import { createContext, useContext, useState, useEffect } from 'react';
import { useGetWhiteLabelConfiguration } from '@src/hooks/queries/white-label';
import ClientApi from '@src/types/client';
import { useAccount } from './auth-context';

interface ConfigurationsContextType {
  configurationData: ClientApi.WhiteLabel.GetWhiteLabelConfigurations.Response | undefined;
  configurationLoading: boolean;
  configurationError: boolean;
  configurationRefetch: () => void;
  isPublished: boolean;
  shouldShowPreview: boolean;
}

const ConfigurationsContext = createContext<ConfigurationsContextType | undefined>(undefined);

export const ConfigurationsProvider = ({ children }: { children: React.ReactNode }) => {
  const [isPublished, setIsPublished] = useState(false);
  const [shouldShowPreview, setShouldShowPreview] = useState(false);

  const { isWhiteLabelBusiness, user } = useAccount();

  const {
    data: configurationData,
    isLoading: configurationLoading,
    isError: configurationError,
    refetch: configurationRefetch
  } = useGetWhiteLabelConfiguration({
    enabled: isWhiteLabelBusiness && user?.userOnboardingStatus === 2
  });

  useEffect(() => {
    if (configurationData) {
      handleSettings();
    }
  }, [configurationData]);

  const handleSettings = () => {
    if (!configurationData) return;
    const configData = configurationData?.data;

    const hasDomain = configData?.domainSettings?.hasDomain || false;
    const shouldShowPreview = hasDomain
      ? !!configData?.domainSettings?.domainName && !!configData?.domainSettings?.dnsSetupCompleted
      : !!configData?.domainSettings?.domainName;

    setIsPublished(configData?.isPublished || false);
    setShouldShowPreview(shouldShowPreview);
  };

  const value: ConfigurationsContextType = {
    configurationData,
    configurationError,
    configurationLoading,
    configurationRefetch,
    shouldShowPreview,
    isPublished
  };
  return <ConfigurationsContext.Provider value={value}>{children}</ConfigurationsContext.Provider>;
};

export const useConfigurationsContext = () => {
  const context = useContext(ConfigurationsContext);
  if (!context) {
    throw new Error('useConfigurationsContext must be used within a ConfigurationsProvider');
  }
  return context;
};
